import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBreadcrumbs } from '../../contexts/BreadcrumbContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  createCareerExperience,
  getCareerExperiences,
  groupExperiencesByTimeline,
  ProposedCareerExperience,
  updateCareerExperience,
  deleteCareerExperience,
} from '../../services/careerMemory';
import { TimelineEntry, CareerExperience } from '../../types/careerMemory';
import { ResumeImportModal } from '../../components/CareerMemory/ResumeImportModal';
import './CareerTimeline.css';

const WEEKLY_PENDING_QUEUE_STORAGE_KEY = 'careerMemory.pendingQueue';

interface PendingExperience extends ProposedCareerExperience {
  pendingId: string;
}

export function CareerTimeline() {
  const navigate = useNavigate();
  const { setPageCrumbs } = useBreadcrumbs();
  const { t } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [timelineEntries, setTimelineEntries] = useState<TimelineEntry[]>([]);
  const [selectedExperience, setSelectedExperience] = useState<CareerExperience | null>(null);
  const [editingExperienceId, setEditingExperienceId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [pendingExperiences, setPendingExperiences] = useState<PendingExperience[]>([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [showResumeImportModal, setShowResumeImportModal] = useState(false);
  const [expandedPendingIds, setExpandedPendingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setPageCrumbs([{ label: t('careerTimeline.title') }]);
  }, [setPageCrumbs, t]);

  useEffect(() => {
    loadTimeline();
  }, []);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(WEEKLY_PENDING_QUEUE_STORAGE_KEY);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setPendingExperiences(parsed);
      }
    } catch (err) {
      console.warn('Failed to read pending queue from sessionStorage:', err);
    }
  }, []);

  const loadTimeline = async () => {
    try {
      setLoading(true);
      const experiences = await getCareerExperiences();
      const entries = groupExperiencesByTimeline(experiences);
      setTimelineEntries(entries);
    } catch (err) {
      console.error('Failed to load timeline:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  const getTypeIcon = (type: string): string => {
    switch (type) {
      case 'project': return '🚀';
      case 'success': return '✨';
      case 'challenge': return '💪';
      case 'role': return '👔';
      case 'achievement': return '🏆';
      case 'skill': return '🎯';
      default: return '📌';
    }
  };

  const closeModal = () => {
    setSelectedExperience(null);
    setEditingExperienceId(null);
    setEditTitle('');
    setEditDescription('');
    setActionError('');
  };

  const persistPendingQueue = (next: PendingExperience[]) => {
    setPendingExperiences(next);
    try {
      if (next.length === 0) {
        sessionStorage.removeItem(WEEKLY_PENDING_QUEUE_STORAGE_KEY);
      } else {
        sessionStorage.setItem(WEEKLY_PENDING_QUEUE_STORAGE_KEY, JSON.stringify(next));
      }
    } catch (err) {
      console.warn('Failed to persist pending queue:', err);
    }
  };

  const savePendingExperience = async (item: PendingExperience) => {
    await createCareerExperience({
      title: item.title,
      description: item.description,
      startDate: item.startDate,
      endDate: item.endDate,
      company: item.company,
      role: item.role,
      type: item.type,
      situation: item.situation,
      task: item.task,
      action: item.action,
      result: item.result,
      skills: item.skills,
      technologies: item.technologies,
      metrics: item.metrics,
      tags: Array.from(new Set([...(item.skills || []), ...(item.technologies || [])])).slice(0, 10),
    });
  };

  const handleApprovePending = async (pendingId: string) => {
    const item = pendingExperiences.find((entry) => entry.pendingId === pendingId);
    if (!item || pendingLoading) {
      return;
    }

    setPendingLoading(true);
    setActionError('');
    try {
      await savePendingExperience(item);
      const remaining = pendingExperiences.filter((entry) => entry.pendingId !== pendingId);
      persistPendingQueue(remaining);
      await loadTimeline();
    } catch (err) {
      console.error('Failed to approve pending timeline item:', err);
      setActionError(t('careerTimeline.errors.approveFailed'));
    } finally {
      setPendingLoading(false);
    }
  };

  const handleDismissPending = (pendingId: string) => {
    if (pendingLoading) {
      return;
    }
    const remaining = pendingExperiences.filter((entry) => entry.pendingId !== pendingId);
    persistPendingQueue(remaining);
  };

  const togglePendingExpand = useCallback((pendingId: string) => {
    setExpandedPendingIds(prev => {
      const next = new Set(prev);
      if (next.has(pendingId)) next.delete(pendingId); else next.add(pendingId);
      return next;
    });
  }, []);

  const handlePendingChange = (pendingId: string, field: 'title' | 'description', value: string) => {
    const next = pendingExperiences.map((entry) =>
      entry.pendingId === pendingId ? { ...entry, [field]: value } : entry
    );
    persistPendingQueue(next);
  };

  const handleApproveAllPending = async () => {
    if (!pendingExperiences.length || pendingLoading) {
      return;
    }

    setPendingLoading(true);
    setActionError('');
    try {
      for (const item of pendingExperiences) {
        await savePendingExperience(item);
      }
      persistPendingQueue([]);
      await loadTimeline();
    } catch (err) {
      console.error('Failed to approve all pending timeline items:', err);
      setActionError(t('careerTimeline.errors.approveFailed'));
    } finally {
      setPendingLoading(false);
    }
  };

  const handleImportFromResume = () => {
    setShowResumeImportModal(true);
  };

  const startEditing = (experience: CareerExperience) => {
    setSelectedExperience(experience);
    setEditingExperienceId(experience.id);
    setEditTitle(experience.title || '');
    setEditDescription(experience.description || '');
    setActionError('');
  };

  const handleSaveEdit = async () => {
    if (!selectedExperience || !editTitle.trim()) {
      return;
    }

    setActionLoading(true);
    setActionError('');
    try {
      await updateCareerExperience(selectedExperience.id, {
        title: editTitle.trim(),
        description: editDescription.trim(),
      });
      await loadTimeline();
      closeModal();
    } catch (error) {
      console.error('Failed to update experience:', error);
      setActionError(t('careerTimeline.errors.updateFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (experience: CareerExperience) => {
    const confirmed = window.confirm(t('careerTimeline.confirmDelete'));
    if (!confirmed) {
      return;
    }

    setActionLoading(true);
    setActionError('');
    try {
      await deleteCareerExperience(experience.id);
      await loadTimeline();
      closeModal();
    } catch (error) {
      console.error('Failed to delete experience:', error);
      setActionError(t('careerTimeline.errors.deleteFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="career-timeline-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('careerTimeline.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="career-timeline-container">
      <div className="timeline-header">
        <div>
          <button
            className="btn btn-secondary timeline-home-btn"
            onClick={() => navigate('/dashboard')}
          >
            ← {t('nav.home')}
          </button>
          <h1>{t('careerTimeline.title')}</h1>
          <p className="subtitle">{t('careerTimeline.subtitle')}</p>
        </div>
        <div className="timeline-header-actions">
          <button
            className="btn btn-secondary"
            onClick={handleImportFromResume}
            disabled={pendingLoading}
            data-testid="timeline-import-resume"
          >
            {t('careerTimeline.importFromResume')}
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/career-interview')}
          >
            + {t('careerTimeline.addMore')}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/career-interview')}
          >
            {t('careerTimeline.startInterview')}
          </button>
        </div>
      </div>

      {pendingExperiences.length > 0 && (
        <div className="pending-queue-card" data-testid="timeline-pending-queue">
          <div className="pending-queue-header">
            <h3>{t('careerTimeline.pending.title')}</h3>
            <button
              className="btn btn-secondary"
              onClick={handleApproveAllPending}
              disabled={pendingLoading}
              data-testid="timeline-approve-all"
            >
              {pendingLoading ? t('common.loading') : t('careerTimeline.pending.approveAll')}
            </button>
          </div>
          <p className="pending-queue-subtitle">
            {t('careerTimeline.pending.count').replace('{{count}}', String(pendingExperiences.length))}
          </p>
          <div className="pending-queue-list">
            {pendingExperiences.map((item) => {
              const isExpanded = expandedPendingIds.has(item.pendingId);
              const hasStarContent = item.situation || item.task || item.action || item.result;
              const allTags = [...(item.skills || []), ...(item.technologies || [])].slice(0, 8);
              return (
                <div key={item.pendingId} className="pending-queue-item" data-testid="timeline-pending-card">
                  {/* Meta row: type icon + company / role / dates */}
                  {(item.company || item.role || item.startDate) && (
                    <div className="pending-meta-row">
                      <span className="pending-type-icon">
                        {item.type === 'role' ? '👔' : item.type === 'project' ? '🚀' : item.type === 'achievement' ? '🏆' : '📌'}
                      </span>
                      {item.company && <span className="pending-company">{item.company}</span>}
                      {item.role && <span className="pending-role">{item.role}</span>}
                      {(item.startDate || item.endDate) && (
                        <span className="pending-dates">
                          {item.startDate || '?'} → {item.endDate || 'Present'}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Editable title */}
                  <input
                    className="pending-input"
                    value={item.title}
                    onChange={(e) => handlePendingChange(item.pendingId, 'title', e.target.value)}
                    placeholder={t('careerTimeline.pending.titleField')}
                    title={t('careerTimeline.pending.titleField')}
                    aria-label={t('careerTimeline.pending.titleField')}
                    disabled={pendingLoading}
                  />

                  {/* Editable description */}
                  <textarea
                    className="pending-textarea"
                    value={item.description}
                    onChange={(e) => handlePendingChange(item.pendingId, 'description', e.target.value)}
                    placeholder={t('careerTimeline.pending.descriptionField')}
                    title={t('careerTimeline.pending.descriptionField')}
                    aria-label={t('careerTimeline.pending.descriptionField')}
                    rows={2}
                    disabled={pendingLoading}
                  />

                  {/* Skills / technology badges */}
                  {allTags.length > 0 && (
                    <div className="pending-tags">
                      {allTags.map((tag, i) => (
                        <span key={i} className="pending-tag">{tag}</span>
                      ))}
                      {((item.skills?.length ?? 0) + (item.technologies?.length ?? 0)) > 8 && (
                        <span className="pending-tag pending-tag--more">
                          +{((item.skills?.length ?? 0) + (item.technologies?.length ?? 0)) - 8}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Expandable STAR section */}
                  {hasStarContent && (
                    <button
                      type="button"
                      className="pending-expand-btn"
                      onClick={() => togglePendingExpand(item.pendingId)}
                    >
                      {isExpanded ? '▲ Hide details' : '▼ Show STAR details'}
                    </button>
                  )}
                  {isExpanded && hasStarContent && (
                    <div className="pending-star-section">
                      {item.situation && (
                        <div className="pending-star-row">
                          <span className="pending-star-label">📍 Situation</span>
                          <p className="pending-star-text">{item.situation}</p>
                        </div>
                      )}
                      {item.task && (
                        <div className="pending-star-row">
                          <span className="pending-star-label">🎯 Task</span>
                          <p className="pending-star-text">{item.task}</p>
                        </div>
                      )}
                      {item.action && (
                        <div className="pending-star-row">
                          <span className="pending-star-label">⚡ Action</span>
                          <p className="pending-star-text">{item.action}</p>
                        </div>
                      )}
                      {item.result && (
                        <div className="pending-star-row">
                          <span className="pending-star-label">🎉 Result</span>
                          <p className="pending-star-text">{item.result}</p>
                        </div>
                      )}
                      {item.metrics && item.metrics.length > 0 && (
                        <div className="pending-star-row">
                          <span className="pending-star-label">📊 Metrics</span>
                          <ul className="pending-metrics-list">
                            {item.metrics.map((m, i) => <li key={i}>{m}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="pending-queue-actions">
                    <button
                      className="btn btn-primary"
                      onClick={() => handleApprovePending(item.pendingId)}
                      disabled={pendingLoading || !item.title?.trim()}
                      data-testid="timeline-approve-item"
                    >
                      {t('careerTimeline.pending.approve')}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleDismissPending(item.pendingId)}
                      disabled={pendingLoading}
                      data-testid="timeline-dismiss-item"
                    >
                      {t('careerTimeline.pending.dismiss')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {timelineEntries.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📝</div>
          <h2>{t('careerTimeline.empty.title')}</h2>
          <p>{t('careerTimeline.empty.description')}</p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/career-interview')}
          >
            {t('careerTimeline.empty.action')}
          </button>
        </div>
      ) : (
        <>
          <div className="timeline-stats">
            <div className="stat-card">
              <div className="stat-value">{timelineEntries.reduce((acc, entry) => acc + entry.experiences.length, 0)}</div>
              <div className="stat-label">{t('careerTimeline.stats.experiences')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{timelineEntries.reduce((acc, entry) => acc + entry.experiences.filter(e => e.type === 'project').length, 0)}</div>
              <div className="stat-label">{t('careerTimeline.stats.projects')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{timelineEntries.reduce((acc, entry) => acc + entry.experiences.filter(e => e.type === 'achievement').length, 0)}</div>
              <div className="stat-label">{t('careerTimeline.stats.achievements')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{new Set(timelineEntries.flatMap(e => e.experiences.map(exp => exp.company))).size}</div>
              <div className="stat-label">{t('careerTimeline.stats.companies')}</div>
            </div>
          </div>

          <div className="timeline-content">
            <div className="timeline">
              {timelineEntries.map((entry, index) => (
                <div key={`${entry.date}-${index}`} className="timeline-entry">
                  <div className="timeline-marker">
                    <div className="timeline-dot" />
                    {index < timelineEntries.length - 1 && <div className="timeline-line" />}
                  </div>

                  <div className="timeline-card">
                    <div className="timeline-date">{formatDate(entry.date)}</div>
                    <h3 className="timeline-title">{entry.title}</h3>
                    <p className="timeline-description">{entry.description}</p>

                    <div className="experiences-list">
                      {entry.experiences.map((exp) => (
                        <div
                          key={exp.id}
                          className="experience-item"
                          onClick={() => setSelectedExperience(exp)}
                        >
                          <div className="experience-header">
                            <span className="experience-icon">{getTypeIcon(exp.type)}</span>
                            <span className="experience-title">{exp.title}</span>
                            <div className="experience-actions">
                              <button
                                type="button"
                                className="btn btn-secondary experience-action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEditing(exp);
                                }}
                              >
                                {t('common.edit')}
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary experience-action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(exp);
                                }}
                                disabled={actionLoading}
                              >
                                {t('common.delete')}
                              </button>
                            </div>
                          </div>
                          <div className="experience-meta">
                            {exp.duration && <span className="meta-item">📅 {exp.duration}</span>}
                            {exp.metrics && exp.metrics.length > 0 && (
                              <span className="meta-item">📊 {exp.metrics[0]}</span>
                            )}
                          </div>
                          {exp.technologies && exp.technologies.length > 0 && (
                            <div className="experience-tech">
                              {exp.technologies.slice(0, 3).map((tech, i) => (
                                <span key={i} className="tech-badge">{tech}</span>
                              ))}
                              {exp.technologies.length > 3 && (
                                <span className="tech-badge">+{exp.technologies.length - 3}</span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {showResumeImportModal && (
        <ResumeImportModal
          onClose={() => setShowResumeImportModal(false)}
          onSaved={() => { loadTimeline(); }}
        />
      )}

      {selectedExperience && (
        <div className="experience-modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {getTypeIcon(selectedExperience.type)} {selectedExperience.title}
              </h2>
              <div className="modal-header-actions">
                {editingExperienceId === selectedExperience.id ? (
                  <>
                    <button
                      className="btn btn-primary"
                      onClick={handleSaveEdit}
                      disabled={actionLoading || !editTitle.trim()}
                    >
                      {actionLoading ? t('common.loading') : t('common.save')}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setEditingExperienceId(null)}
                      disabled={actionLoading}
                    >
                      {t('common.cancel')}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="btn btn-secondary"
                      onClick={() => startEditing(selectedExperience)}
                      disabled={actionLoading}
                    >
                      {t('common.edit')}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleDelete(selectedExperience)}
                      disabled={actionLoading}
                    >
                      {t('common.delete')}
                    </button>
                  </>
                )}
                <button className="modal-close" onClick={closeModal}>
                  ×
                </button>
              </div>
            </div>

            <div className="modal-body">
              {actionError && <div className="timeline-action-error">{actionError}</div>}

              {editingExperienceId === selectedExperience.id && (
                <div className="modal-section edit-form-section">
                  <label className="edit-label" htmlFor="edit-title">{t('careerTimeline.editForm.title')}</label>
                  <input
                    id="edit-title"
                    className="edit-input"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                  />

                  <label className="edit-label" htmlFor="edit-description">{t('careerTimeline.editForm.description')}</label>
                  <textarea
                    id="edit-description"
                    className="edit-textarea"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    rows={4}
                  />
                </div>
              )}

              <div className="modal-section">
                <div className="modal-meta">
                  {selectedExperience.company && (
                    <span><strong>{t('careerTimeline.modal.company')}:</strong> {selectedExperience.company}</span>
                  )}
                  {selectedExperience.role && (
                    <span><strong>{t('careerTimeline.modal.role')}:</strong> {selectedExperience.role}</span>
                  )}
                  {selectedExperience.duration && (
                    <span><strong>{t('careerTimeline.modal.duration')}:</strong> {selectedExperience.duration}</span>
                  )}
                </div>
              </div>

              <div className="modal-section">
                <h3>{t('careerTimeline.modal.description')}</h3>
                <p>{selectedExperience.description}</p>
              </div>

              {selectedExperience.situation && (
                <div className="modal-section star-section">
                  <h3>📍 {t('careerTimeline.modal.situation')}</h3>
                  <p>{selectedExperience.situation}</p>
                </div>
              )}

              {selectedExperience.task && (
                <div className="modal-section star-section">
                  <h3>🎯 {t('careerTimeline.modal.task')}</h3>
                  <p>{selectedExperience.task}</p>
                </div>
              )}

              {selectedExperience.action && (
                <div className="modal-section star-section">
                  <h3>⚡ {t('careerTimeline.modal.action')}</h3>
                  <p>{selectedExperience.action}</p>
                </div>
              )}

              {selectedExperience.result && (
                <div className="modal-section star-section">
                  <h3>🎉 {t('careerTimeline.modal.result')}</h3>
                  <p>{selectedExperience.result}</p>
                </div>
              )}

              {selectedExperience.technologies && selectedExperience.technologies.length > 0 && (
                <div className="modal-section">
                  <h3>{t('careerTimeline.modal.technologies')}</h3>
                  <div className="tech-tags">
                    {selectedExperience.technologies.map((tech, i) => (
                      <span key={i} className="tech-tag">{tech}</span>
                    ))}
                  </div>
                </div>
              )}

              {selectedExperience.skills && selectedExperience.skills.length > 0 && (
                <div className="modal-section">
                  <h3>{t('careerTimeline.modal.skills')}</h3>
                  <div className="tech-tags">
                    {selectedExperience.skills.map((skill, i) => (
                      <span key={i} className="skill-tag">{skill}</span>
                    ))}
                  </div>
                </div>
              )}

              {selectedExperience.metrics && selectedExperience.metrics.length > 0 && (
                <div className="modal-section">
                  <h3>{t('careerTimeline.modal.metrics')}</h3>
                  <ul className="metrics-list">
                    {selectedExperience.metrics.map((metric, i) => (
                      <li key={i}>{metric}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
